"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Line, LineChart, Bar, BarChart } from "recharts";
import { Shield, Network, Cpu, Zap, Terminal, Activity } from "lucide-react";
const cpuData = [{
  time: "Mon",
  usage: 1200,
  standard: 800
}, {
  time: "Tue",
  usage: 1500,
  standard: 950
}, {
  time: "Wed",
  usage: 1100,
  standard: 850
}, {
  time: "Thu",
  usage: 1800,
  standard: 1100
}, {
  time: "Fri",
  usage: 2100,
  standard: 1300
}, {
  time: "Sat",
  usage: 2400,
  standard: 1500
}, {
  time: "Sun",
  usage: 1900,
  standard: 1200
}];
const cpuConfig = {
  usage: {
    label: "Explorer Traffic",
    color: "var(--chart-1)"
  },
  standard: {
    label: "Average Blog",
    color: "var(--muted-foreground)"
  }
};
const latencyData = [{
  packet: "Jan",
  latency: 98,
  standard: 85
}, {
  packet: "Feb",
  latency: 99,
  standard: 88
}, {
  packet: "Mar",
  latency: 99.5,
  standard: 90
}, {
  packet: "Apr",
  latency: 99.2,
  standard: 87
}, {
  packet: "May",
  latency: 99.8,
  standard: 91
}, {
  packet: "Jun",
  latency: 99.9,
  standard: 89
}];
const latencyConfig = {
  latency: {
    label: "Uptime %",
    color: "var(--chart-2)"
  },
  standard: {
    label: "Industry Avg",
    color: "var(--muted-foreground)"
  }
};
const memoryData = [{
  cycle: "Photography",
  footprint: 85
}, {
  cycle: "Writing",
  footprint: 92
}, {
  cycle: "Research",
  footprint: 78
}, {
  cycle: "Curation",
  footprint: 95
}, {
  cycle: "Community",
  footprint: 88
}];
const memoryConfig = {
  footprint: {
    label: "Engagement Score",
    color: "var(--chart-3)"
  }
};
const features = [{
  title: "Optimized Content Delivery",
  tagline: "Lightning Fast Insights.",
  description: "Our digital architecture is built for speed. Every travel log and high-resolution gallery is served through a global edge network, ensuring that your journey through our archives is as seamless as a morning breeze in the Alps.",
  icon: Zap,
  chartType: "cpu",
  accentColor: "text-primary",
  accentBg: "bg-primary/5",
  accentBorder: "border-primary/20"
}, {
  title: "Reliable Archive Access",
  tagline: "Always Available, Always On.",
  description: "We maintain a 99.9% uptime record for our digital library. Whether you are browsing from a remote trail or a city cafe, our cloud-native infrastructure ensures that the stories you love are never more than a click away.",
  icon: Shield,
  chartType: "latency",
  accentColor: "text-primary",
  accentBg: "bg-primary/5",
  accentBorder: "border-primary/20"
}, {
  title: "Deep Engagement Metrics",
  tagline: "Data-Driven Storytelling.",
  description: "We don't just write; we analyze what resonates. By tracking engagement across different content pillars, we refine our toolkit to bring you more of the photography and narratives that spark your wanderlust.",
  icon: Activity,
  chartType: "memory",
  accentColor: "text-primary",
  accentBg: "bg-primary/5",
  accentBorder: "border-primary/20"
}];
const backgroundCodeSnippet = `

const blogEngine = {
  performance: "High",
  aesthetic: "Modern Serene",
  typography: "Playfair Display",
  accent: "Sky Blue",
  render() {
    return "A world of discovery.";
  }
};
`;
export default function TheExplorerSToolkit() {
  return <section data-section-id="222684" id="the-explorers-toolkit" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="absolute top-1/4 left-[-10%] h-[500px] w-[500px] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute right-[-10%] bottom-1/4 h-[400px] w-[400px] rounded-lg bg-secondary/5 blur-[100px]" />
        
        <pre className="absolute top-10 right-10 rotate-12 font-mono text-[10px] leading-relaxed text-muted-foreground/20 md:text-xs">
          {backgroundCodeSnippet}
        </pre>
        <pre className="absolute bottom-10 left-10 -rotate-6 font-mono text-[10px] leading-relaxed text-muted-foreground/20 md:text-xs">
          {backgroundCodeSnippet}
        </pre>
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.05]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-secondary px-3 py-1 text-sm font-medium text-primary">
            <Terminal className="h-4 w-4" />
            <span className="font-mono uppercase tracking-wider">System Diagnostics</span>
          </div>
          <h2 className="font-serif mb-6 text-4xl font-semibold tracking-tight md:text-5xl lg:text-6xl">
            The Explorer's <span className="text-primary">Toolkit</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground font-default">
            Behind every story is a robust technical foundation. We leverage modern architecture to ensure your reading experience is fast, secure, and beautiful.
          </p>
        </div>

        <div className="flex flex-col gap-24">
          {features.map((feature, i) => <div key={feature.title} data-index={i} className={`flex flex-col gap-8 md:items-center ${i % 2 !== 0 ? "md:flex-row-reverse" : "md:flex-row"}`}>
              {}
              <div className="flex-1 space-y-6">
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-lg border ${feature.accentBorder} ${feature.accentBg}`}>
                  <feature.icon className={`h-6 w-6 ${feature.accentColor}`} />
                </div>
                <div>
                  <h3 className="font-serif mb-2 text-2xl font-semibold md:text-3xl text-foreground">
                    {feature.title}
                  </h3>
                  <p className={`mb-4 text-xs font-bold tracking-widest uppercase font-mono ${feature.accentColor}`}>
                    {feature.tagline}
                  </p>
                  <p className="text-muted-foreground leading-relaxed font-default">
                    {feature.description}
                  </p>
                </div>
                <div className="pt-4">
                  <Button asChild variant="outline" className="group border-primary/30 bg-background hover:bg-secondary hover:text-primary rounded-lg transition-all duration-400">
                    <Link to="/TheArchivePage">
                      Explore the Archive
                      <Activity className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              <div className="flex-1">
                <Card className={`overflow-hidden border-primary/10 bg-white shadow-md rounded-lg transition-all duration-400 hover:shadow-lg`}>
                  <CardHeader className="border-b border-border/50 bg-secondary/30 pb-4">
                    <CardTitle className="font-mono flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                      <Activity className="h-4 w-4" />
                      Live Performance Data
                    </CardTitle>
                    <CardDescription className="font-default text-muted-foreground">Real-time metrics from our global nodes</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    {feature.chartType === "cpu" && <ChartContainer config={cpuConfig} className="h-[250px] w-full">
                        <AreaChart data={cpuData} margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0
                  }}>
                          <defs>
                            <linearGradient id="fillUsage" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="var(--color-usage)" stopOpacity={0.2} />
                              <stop offset="95%" stopColor="var(--color-usage)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                          <XAxis dataKey="time" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Area type="monotone" dataKey="standard" stroke="var(--muted-foreground)" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" />
                          <Area type="monotone" dataKey="usage" stroke="var(--color-usage)" fill="url(#fillUsage)" strokeWidth={2.5} />
                        </AreaChart>
                      </ChartContainer>}

                    {feature.chartType === "latency" && <ChartContainer config={latencyConfig} className="h-[250px] w-full">
                        <LineChart data={latencyData} margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0
                  }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                          <XAxis dataKey="packet" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} domain={[80, 100]} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Line type="monotone" dataKey="standard" stroke="var(--muted-foreground)" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                          <Line type="monotone" dataKey="latency" stroke="var(--color-latency)" strokeWidth={2.5} dot={{
                      r: 4,
                      fill: "var(--primary)",
                      strokeWidth: 0
                    }} activeDot={{
                      r: 6
                    }} />
                        </LineChart>
                      </ChartContainer>}

                    {feature.chartType === "memory" && <ChartContainer config={memoryConfig} className="h-[250px] w-full">
                        <BarChart data={memoryData} margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0
                  }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                          <XAxis dataKey="cycle" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                          <ChartTooltip content={<ChartTooltipContent />} cursor={{
                      fill: 'var(--secondary)',
                      opacity: 0.4
                    }} />
                          <Bar dataKey="footprint" fill="var(--color-footprint)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ChartContainer>}
                  </CardContent>
                </Card>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}